import { useState, useEffect, useMemo, useCallback, useRef, useContext } from "react";
import Image from "../../components/image/Image";
import MainHeader from "../../components/header/MainHeader";
import { useNavigate } from "react-router-dom";
import "./dashboard.css";
import { useTranslation } from "react-i18next";
import Logo from "../../components/logo/Logo";
import Toast from '../../components/common/Toast';
import { useModal } from "../../context/ModalContext";
import { useAuth } from "../../context/AuthContext";
import { ThemeContext } from "../../context/ThemeContext";
import api from "../../api/axios";

// Option keys and labels: shared with admin manage page (see utils/dashboardOptions.js)
import { DASHBOARD_OPTION_KEYS } from "../../utils/dashboardOptions";

// Re-export for consumers that still import from here
export { DASHBOARD_OPTION_KEYS };

// Build full option config for a given key (used for mapping API keys to UI)
const getOptionConfig = (key, trans) => {
  const configs = {
    seller: {
      icon: <img src={Image.dashSeller} alt="Seller Calculator" width={22} className="ic-blue" />,
      title: trans("dashboard_option_seller_title"),
      description: trans("dashboard_option_seller_desc"),
      highlighted: true,
      link: "/seller",
    },
    buyer: {
      icon: <img src={Image.dashBuyer} alt="Buyer Calculator" width={32} className="ic-blue" />,
      title: trans("dashboard_option_buyer_title"),
      description: trans("dashboard_option_buyer_desc"),
      link: "/buyer",
    },
    quick: {
      icon: <img src={Image.quickcalc} alt="Quick Calculator" width={30} className="ic-blue" />,
      title: "Quick Calculator",
      description: "Access calculator directly",
      link: "/quick",
    },
    seller2net: {
      icon: <img src={Image.dashSellertwo} alt="Seller 2 Net" width={28} className="ic-blue" />,
      title: trans("dashboard_option_seller2_title"),
      description: trans("dashboard_option_seller2_desc"),
      link: "/seller2net",
    },
    refinance: {
      icon: <img src={Image.dashRefinance} alt="Refinance Calculator" width={35} className="ic-blue" />,
      title: trans("dashboard_option_refinance_title"),
      description: trans("dashboard_option_refinance_desc"),
      link: "/refinance",
    },
    propnine: {
      icon: <img src={Image.proposition} alt="Proposition 19" width={30} className="ic-blue" />,
      title: trans("Proposition 19"),
      description: trans("California"),
      link: "/propnine",
    },
    open: {
      icon: <img src={Image.dashOpen} alt="Open Calculator" width={25} className="ic-blue" />,
      title: trans("dashboard_option_open_title"),
      description: trans("dashboard_option_open_desc"),
      link: "/open",
    },
  };
  return configs[key] || null;
};

// ✅ Dynamic greeting based on time, localized
const getGreeting = (t) => {
  const hour = new Date().getHours();
  if (hour < 12) return t("dashboard_greeting_morning");
  if (hour < 18) return t("dashboard_greeting_afternoon");
  return t("dashboard_greeting_evening");
};

export default function DashboardPage() {
  const { t: trans } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useContext(ThemeContext);
  const { openModal, closeModal, closeAllModals } = useModal();
  const [toast, setToast] = useState({ message: "", visible: false, type: 'success' });
  const [isUpdating, setIsUpdating] = useState(false);
  const [dashboardOptionKeys, setDashboardOptionKeys] = useState(null);
  const toastTimeoutRef = useRef(null);

  // Fetch dashboard options for current main company (theme.id).
  // Backend: GET /auth/dashboard-options?maincompanyid=<id> returns
  //   { status: "success", data: ["seller", "buyer", ...], options: [...] }
  useEffect(() => {
    let cancelled = false;
    const maincompanyid = theme?.id;

    const fetchOptions = async () => {
      if (!maincompanyid) {
        setDashboardOptionKeys(DASHBOARD_OPTION_KEYS);
        return;
      }
      try {
        const { data } = await api.get("/auth/dashboard-options", {
          params: { maincompanyid },
        });
        const raw = data?.data ?? data?.options ?? data;
        const list = Array.isArray(raw)
          ? raw.filter((k) => k && DASHBOARD_OPTION_KEYS.includes(k))
          : [];
        if (!cancelled) {
          setDashboardOptionKeys(list);
        }
      } catch (err) {
        if (!cancelled) {
          setDashboardOptionKeys(DASHBOARD_OPTION_KEYS);
        }
      }
    };

    fetchOptions();
    return () => { cancelled = true; };
  }, [theme?.id]);

  // Check for notify_user flag on mount
  useEffect(() => {
    const notifyUser = localStorage.getItem("notify_user");
    if (notifyUser === "true") {
      showUpdateModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const handleUpdateToaster = useCallback(({ message, visible, type = 'success' }) => {
    setToast({ message, visible, type });
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    if (visible) {
      toastTimeoutRef.current = setTimeout(() => {
        setToast({ message: "", visible: false, type: 'success' });
      }, 5000);
    }
  }, []);

  const handleRemindLater = useCallback(() => {
    localStorage.removeItem("notify_user");
    closeModal();
  }, [closeModal]);

  const handleUpdate = useCallback(async () => {
    try {
      setIsUpdating(true);
      const res = await api.get('/download-user-template');

      if (res?.data?.status === "success" && res.data.data) {
        localStorage.removeItem("notify_user");
        // Close all modals to ensure the update modal is closed
        closeAllModals();
        // Show toast after modal closes
        setTimeout(() => {
          handleUpdateToaster({ 
            message: "User settings downloaded and applied successfully.", 
            visible: true, 
            type: 'success' 
          });
        }, 150);
      } else {
        handleUpdateToaster({ 
          message: "Failed to update user settings.", 
          visible: true, 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error("Error updating user settings:", error);
      handleUpdateToaster({ 
        message: "Failed to update settings. Please try again later.", 
        visible: true, 
        type: 'error' 
      });
    } finally {
      setIsUpdating(false);
    }
  }, [handleUpdateToaster, closeAllModals]);

  const showUpdateModal = useCallback(() => {
    openModal({
      title: "Updated Closing Cost",
      size: "smallmodel",
      content: (
        <div className="modelspace application-fe">
          <p className="model-desc-details notice-icon">
            New Updated Closing Costs are available. Please Update.
          </p>
          <div className="frm-btn popup-2-btn">
            <button
              className="submit-btn btn-bg inpt-btn btn-discard"
              onClick={handleRemindLater}
            >
              Remind me later
            </button>
            <button
              className="submit-btn btn-bg inpt-btn"
              onClick={handleUpdate}
              disabled={isUpdating}
            >
              {isUpdating ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      ),
    });
  }, [openModal, isUpdating, handleRemindLater, handleUpdate]);

  const openCalculation = useCallback((route) => {
    closeModal();
    navigate(`${route}?mode=quick`, {
      state: { source: "dashboard" }
    });
  }, [closeModal, navigate]);

  const handleCalculator = useCallback((route) => {
    if (route === "/quick") {
      openModal({
        title: "Quick Calculator",
        size: "smallmodel",
        content: (
          <div className="modelspace application-fe">
            <p className="model-desc-details notice-icon">
              A Quick Calculator is available for this Net Sheet. It generates an estimate using standard transaction details and state-specific defaults to provide a faster estimate.
            </p>
            <p className="model-desc-details paddleft">
              <b>How would you like to proceed?</b>
            </p>
            <div className="frm-btn popup-2-btn">
              <button
                className="submit-btn btn-bg inpt-btn"
                onClick={() => openCalculation("/buyer")}
              >
                Buyer
              </button>
              <button
                className="submit-btn btn-bg inpt-btn btn-discard"
                onClick={() => openCalculation("/seller")}
              >
                Seller
              </button>
            </div>
          </div>
        ),
      });
      return;
    }

    navigate(route, {
      state: { source: "dashboard" }
    });
  }, [openModal, navigate, openCalculation]);

  // Build options from backend keys (order and subset per main company); fallback to all options
  const options = useMemo(() => {
    const keys = dashboardOptionKeys || DASHBOARD_OPTION_KEYS;
    let list = keys
      .map((key) => getOptionConfig(key, trans))
      .filter(Boolean);

    // Show Proposition 19 option only for users with state === 5
    if (user?.state !== 5) {
      list = list.filter((opt) => opt.link !== "/propnine");
    }

    return list;
  }, [dashboardOptionKeys, trans, user?.state]);

  return (
    <div className="dashboard-layout">
      <MainHeader />
      <div className="dashboard-inner scroll-box">
        <div className="left-side-dash">
          
            {theme?.logo_dash ? (
              <div className="compny-logo-dash"><Logo type="primaryDash" size={196} /></div>
            ) : (
              <div className="compny-logo"><Logo type="primary" size={196} /></div>
            )}
          
          <div className="dashboard-greeting">

            <h1>
              {getGreeting(trans)},{" "}
              {user?.first_name || trans("dashboard_guest")} 👋
            </h1>
            <p>
              {trans("dashboard_welcome_text_line1")}
              <br />
              {trans("dashboard_welcome_text_line2")}
            </p>
          </div>
        </div>

        <div className="right-side-dash">
          <ul>
            {options.map((opt) => (
              <li key={opt.link}>
                <div 
                  className="dash-card"
                  onClick={() => handleCalculator(opt.link)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCalculator(opt.link);
                    }
                  }}
                >
                  <div className="list-d-img theme-clr">{opt.icon}</div>
                  <div className="list-d-cont">
                    <h3>{opt.title}</h3>
                    <p>{opt.description}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />

    </div>
  );
}
